// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Region, Institution, Channel } from './types';
import { esc, num, rate, delta, num as n2, CHANNEL_COLOUR, CHANNEL_LABEL, REMOTENESS_SHORT } from './format';
import { niceMax } from './charts';

/** Vertical year bars for one channel series. */
function yearBars(years: number[], values: number[], colour: string, unitLabel: string): string {
  const w = 480, h = 140, m = { t: 10, r: 8, b: 20, l: 34 };
  const iw = w - m.l - m.r, ih = h - m.t - m.b;
  const yMax = niceMax(Math.max(...values, 1));
  const bw = iw / years.length;
  let bars = '';
  years.forEach((yr, i) => {
    const bh = (values[i] / yMax) * ih;
    const x = m.l + i * bw + bw * 0.12;
    const y = m.t + ih - bh;
    bars += `<rect class="bar" x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${(bw * 0.76).toFixed(1)}" height="${Math.max(0, bh).toFixed(1)}" rx="1.5" fill="${colour}" data-tip="${esc(`${yr}: ${num(values[i])} ${unitLabel}`)}" />`;
    if (i === 0 || i === years.length - 1) bars += `<text class="axis-text" x="${(x + bw * 0.38).toFixed(1)}" y="${(m.t + ih + 14).toFixed(1)}" text-anchor="middle">${yr}</text>`;
  });
  let grid = '';
  for (let t = 0; t <= 2; t++) {
    const v = (yMax / 2) * t;
    const yy = m.t + ih - (v / yMax) * ih;
    grid += `<line class="grid-line" x1="${m.l}" y1="${yy.toFixed(1)}" x2="${m.l + iw}" y2="${yy.toFixed(1)}" /><text class="axis-text" x="${m.l - 5}" y="${(yy + 3).toFixed(1)}" text-anchor="end">${num(v)}</text>`;
  }
  return `<div class="chart-wrap"><svg class="chart" viewBox="0 0 ${w} ${h}" role="img" aria-label="${esc(unitLabel)} by year">${grid}${bars}</svg></div>`;
}

export function renderRegionDrawer(el: HTMLElement, r: Region, data: Dataset): void {
  const years = data.national.years;
  const ranked = [...data.regions].filter((x) => x.perCapita != null).sort((a, b) => (b.perCapita! - a.perCapita!));
  const rank = ranked.findIndex((x) => x.code === r.code) + 1;
  const lossRanked = [...data.regions].sort((a, b) => a.branchChangeAbs - b.branchChangeAbs);
  const lossRank = lossRanked.findIndex((x) => x.code === r.code) + 1;

  const tag = r.lostLast
    ? `<span class="tag lost">Lost its last branch</span>`
    : r.branchNow === 0 && r.everHadBranch
      ? `<span class="tag lost">No branches left</span>`
      : '';

  el.innerHTML = `
    <button class="icon-btn drawer-close" aria-label="Close">✕</button>
    <h2>${esc(r.name)} ${tag}</h2>
    <p class="sub">${esc(r.state ?? '')}${r.remoteness ? ' · ' + esc(REMOTENESS_SHORT[r.remoteness] ?? r.remoteness) : ''} · SA3 ${esc(r.code)}</p>
    <dl class="kv">
      <dt>Branches now (${years[years.length - 1]})</dt><dd>${num(r.branchNow)}</dd>
      <dt>Branches in ${years[0]}</dt><dd>${num(r.branch2017)}</dd>
      <dt>Change</dt><dd class="${r.branchChangeAbs < 0 ? 'down' : r.branchChangeAbs > 0 ? 'up' : ''}">${r.branchChange == null ? '—' : delta(r.branchChange)}</dd>
      <dt>Branches per 10,000 residents</dt><dd>${rate(r.perCapita, 2)}</dd>
      <dt>Rank by branch access</dt><dd>${rank || '—'} of ${ranked.length}</dd>
      <dt>Rank by branches lost</dt><dd>${lossRank || '—'} of ${lossRanked.length}</dd>
      <dt>Bank@Post now</dt><dd>${num(r.bankpostNow)}</dd>
      <dt>Bank ATMs now</dt><dd>${num(r.atmNow)}</dd>
      <dt>Other face-to-face</dt><dd>${num(r.otherNow)}</dd>
      <dt>Resident population</dt><dd>${num(r.pop)}</dd>
    </dl>
    <h3>Branches, ${years[0]}–${years[years.length - 1]}</h3>
    ${yearBars(years, r.series.branch, CHANNEL_COLOUR.branch, 'branches')}
    <h3>Bank@Post, ${years[0]}–${years[years.length - 1]}</h3>
    ${yearBars(years, r.series.bankpost, CHANNEL_COLOUR.bankpost, 'Bank@Post outlets')}
    <p class="mini-note">${r.lostLast ? 'This region no longer has a staffed bank branch. ' : ''}Physical service points reported to APRA at 30 June each year. EFTPOS terminals excluded.</p>
  `;
}

export function renderInstitutionDrawer(el: HTMLElement, it: Institution, data: Dataset): void {
  const years = data.national.years;
  const sectorLabel = data.national.sectorLabels[it.sector] ?? it.sector;
  const channels: Channel[] = ['branch', 'bankpost', 'other', 'atm'];
  const mix = channels
    .filter((ch) => it.series[ch][years.length - 1] > 0)
    .map((ch) => `<span class="legend-item"><span class="legend-swatch" style="background:${CHANNEL_COLOUR[ch]}"></span>${CHANNEL_LABEL[ch]}: <span class="num">${n2(it.series[ch][years.length - 1])}</span></span>`)
    .join('');

  el.innerHTML = `
    <button class="icon-btn drawer-close" aria-label="Close">✕</button>
    <h2>${esc(it.name)}</h2>
    <p class="sub">${esc(sectorLabel)}</p>
    <dl class="kv">
      <dt>Branches now (${years[years.length - 1]})</dt><dd>${num(it.branchNow)}</dd>
      <dt>Branches in ${years[0]}</dt><dd>${num(it.branch2017)}</dd>
      <dt>Peak branches</dt><dd>${num(it.branchPeak)}</dd>
      <dt>Change since ${years[0]}</dt><dd class="${it.branchChangeAbs < 0 ? 'down' : it.branchChangeAbs > 0 ? 'up' : ''}">${it.branch2017 ? delta(it.branchChange) : '—'} (${it.branchChangeAbs > 0 ? '+' : ''}${num(it.branchChangeAbs)})</dd>
      <dt>Bank ATMs now</dt><dd>${num(it.atmNow)}</dd>
      <dt>Other face-to-face</dt><dd>${num(it.otherNow)}</dd>
      <dt>Bank@Post</dt><dd>${num(it.bankpostNow)}</dd>
    </dl>
    <h3>Branches, ${years[0]}–${years[years.length - 1]}</h3>
    ${yearBars(years, it.series.branch, CHANNEL_COLOUR.branch, 'branches')}
    <div class="legend">${mix}</div>
    <p class="mini-note">Physical service points reported to APRA at 30 June each year.</p>
  `;
}
